if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.currentPopupId = null;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });

        this.addEventListener('click', this.handleAppButtonClick.bind(this));
        this.addEventListener('change', (event) => {
          if (
            event.target.matches(
              'input[name="id"], select[name="id"], .product-form input, .variant-selects input, .variant-selects select'
            )
          ) {
            this.syncVariantToAppBlock();
          }
        });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data?.variant?.id) {
              this.syncVariantToAppBlock(data.variant.id);
            }
          });
        }
      }

      handleAppButtonClick(event) {
        const trigger = event.target.closest(
          '[data-buy-now-trigger], .cod-buy-now-popup__trigger, [data-cod-buy-now-open], [data-cst-buy-now-button]'
        );
        if (!trigger) return;

        const block =
          trigger.closest('[data-buy-now-block], .cod-buy-now-popup-block') ||
          this.modalContent.querySelector('[data-buy-now-block], .cod-buy-now-popup-block');
        if (!block) return;

        event.preventDefault();
        event.stopPropagation();

        if (!block._codBnInitialized && block.dataset.codBnInitialized !== 'true') {
          document.dispatchEvent(
            new CustomEvent('shopify:section:load', {
              bubbles: true,
              detail: { target: this.modalContent, sectionId: block.dataset.buyNowBlock },
            })
          );
        }

        this.syncVariantToAppBlock();

        block.dispatchEvent(
          new CustomEvent('cst:open-popup-internal', {
            detail: {
              launchMode: 'product_only',
              ignoreDisabled: true,
              syncFromPage: false,
              previewMode: false,
            },
            bubbles: true,
          })
        );

        const popupId = trigger.getAttribute('aria-controls') || block.querySelector('[data-buy-now-popup]')?.id;
        if (popupId) {
          this.currentPopupId = popupId;
        }
      }

      syncVariantToAppBlock(variantId) {
        const block = this.modalContent.querySelector('[data-buy-now-block], .cod-buy-now-popup-block');
        if (!block) return;

        const currentVariantId = variantId || this.querySelector('input[name="id"]')?.value;
        if (currentVariantId) {
          block.dataset.variantGid = `gid://shopify/ProductVariant/${currentVariantId}`;
          const variantField = block.querySelector('[data-buy-now-variant-field]');
          if (variantField) variantField.value = currentVariantId;
          const payOnlineField = block.querySelector('[data-buy-now-pay-online-variant-field]');
          if (payOnlineField) payOnlineField.value = currentVariantId;
        }
        document.dispatchEvent(new CustomEvent('variant:change', { bubbles: true }));
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);

        if (this.currentPopupId) {
          const popup = document.getElementById(this.currentPopupId);
          if (popup && popup.parentNode === document.body) {
            popup.remove();
          }
          this.currentPopupId = null;
        }

        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            document.dispatchEvent(
              new CustomEvent('shopify:section:load', {
                bubbles: true,
                detail: { target: this.modalContent, sectionId: productElement.dataset.section },
              })
            );

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
